import React from 'react';

import {
    ActionStatus, GamePlayerStatus, TurnStatus,
} from '../../../Constants';
import {
    available_action_hex_actions,
} from '../../TwoD/Common/ActionHexUtilities';

import { ActionHex } from './ActionHex';
import { ActionHexDial } from './ActionHexDial';
import { ActionPicker } from './ActionPicker';
import { ActionWrapper } from './ActionWrapper';
import { CurrentAction, currentActionIds } from './CurrentAction';
import { EndTurnButton } from './EndTurnButton';

import './ActionWindow.css';

// Mode table (mirrors ActionRouter.js:198-231 TAKING_TURN/WAITING):
//
//   status=TAKING_TURN, turn=SELECT_ACTION → 'select'  (picker only)
//   status=TAKING_TURN, turn∈{OFF, CHOOSE_ACTIONS,
//       END_TURN, ACTION_COMPLETE, LOCKED}  → 'chooser' (hex + picker)
//   status=WAITING                          → 'waiting' (hex + picker)
//   anything else                           → 'idle'    (renders null)
//
// SELECT_ACTION omits the hex deliberately: the hex is locked once
// an action stack has been chosen — the player is now picking which
// action to fire within that stack via the picker. The hex stays
// visible across CHOOSE_ACTIONS / END_TURN / ACTION_COMPLETE /
// LOCKED so the player can see the current rotation while bonus
// actions resolve.
function computeMode(gamePlayerData) {
    if (!gamePlayerData) return 'idle';
    const status = gamePlayerData.status;
    const turnStatus = gamePlayerData.turn_status;
    if (status === GamePlayerStatus.TAKING_TURN_ID) {
        if (turnStatus === TurnStatus.SELECT_ACTION_ID) return 'select';
        if (turnStatus === TurnStatus.OFF_ID
                || turnStatus === TurnStatus.CHOOSE_ACTIONS_ID
                || turnStatus === TurnStatus.END_TURN_ID
                || turnStatus === TurnStatus.ACTION_COMPLETE_ID
                || turnStatus === TurnStatus.LOCKED_ID) {
            return 'chooser';
        }
        return 'idle';
    }
    if (status === GamePlayerStatus.WAITING_ID) return 'waiting';
    return 'idle';
}

// Mid-turn turn statuses where End Turn is meaningful as an
// explicit yield. Mirrors ActionSelector's MID_TURN_STATUSES so the
// in-picker fallback and the window-level button agree on the
// general case.
const MID_TURN_STATUSES = new Set([
    TurnStatus.CHOOSE_ACTIONS_ID,
    TurnStatus.SELECT_ACTION_ID,
    TurnStatus.ACTION_COMPLETE_ID,
    TurnStatus.LOCKED_ID,
]);

// Statuses that drop the player into a juiced sub-state mid-turn.
// During these, ActionSelector returns the empty fragment because
// status !== TAKING_TURN_ID, so the in-picker control never appears —
// but the player is mid-action (banked Snohe / committed a target)
// and needs a way to bail out of the in-progress action. The
// window-level control covers this case with a SKIP affordance.
//
// State-honest correction (CC_ENDTURN_STATE_SETTLE, 2026-06-08): the
// click on this path was NEVER an end_turn(). A click here routes
// status → action_router → receive_action, which keys on the OUTER
// package type 'Next' → skip_button → finish_action (abandon the
// in-progress action, land at ACTION_COMPLETE). The inner
// data.type='EndTurn' is never inspected on the juiced path, so the
// old 'End Turn' label lied — the behavior was always Skip. The
// earlier comment here claimed "the engine rejects clickEndTurn from
// invalid states already"; forensically false (engine never rejects
// the click — it silently skips), corrected per Michael's ruling.
// Full yield from mid-action is therefore Skip (this control) then
// End Turn (the turn-level control once back at ACTION_COMPLETE) —
// two honest clicks matching the engine's real semantics, no BE
// change.
const JUICED_SUB_STATUSES = new Set([
    GamePlayerStatus.LODGING_ID,
    GamePlayerStatus.GHOSTING_ID,
    GamePlayerStatus.RESEARCHING_ID,
    GamePlayerStatus.RENOVATING_ID,
    GamePlayerStatus.ADVERTISING_ID,
]);

const JUICED_SUB_ACTION_STATUSES = new Set([
    ActionStatus.WAIT_FOR_JUICE,
    ActionStatus.WAIT_FOR_TARGET_ID,
    ActionStatus.WAIT_ID,
    ActionStatus.ADD_ID,
    ActionStatus.REMOVE_ID,
]);

function isHexExhausted(gamePlayerData) {
    if (!gamePlayerData) return false;
    const stacks = available_action_hex_actions(
        gamePlayerData.station,
        gamePlayerData.robot_assignments,
        gamePlayerData.action_hex_position,
        gamePlayerData.locked_action_stacks,
        gamePlayerData.available_actions);
    return stacks.every((stack) => stack.every(
        (action) => action === null || action === undefined));
}

// COPY PLACEHOLDER (CC_ENDTURN_STATE_SETTLE): the mid-action control
// is a Skip, not an End Turn — wording awaiting Michael's final call.
// Its own data-testid base keeps it distinguishable from the
// turn-level End Turn affordance in tests and the DOM.
const MID_ACTION_SKIP_LABEL = 'Skip';
const MID_ACTION_SKIP_TESTID_BASE = 'inline-skip';

// Turn-level End Turn gate. The control is a genuine End Turn ONLY in
// TAKING_TURN mid-turn statuses, where a click routes
// status → turn_click_handler → next_button, whose EndTurn branch is
// unconditional and fires end_turn(). active_player gate is
// non-negotiable. (The juiced sub-states are NOT End Turn — see
// shouldShowMidActionSkip; that split is the 2026-06-08 state-honest
// correction.)
function shouldShowEndTurn(gamePlayerData, gameData) {
    if (!gamePlayerData || !gameData) return false;
    if (gameData.active_player !== gamePlayerData.id) return false;
    if (gamePlayerData.status !== GamePlayerStatus.TAKING_TURN_ID) {
        return false;
    }
    return MID_TURN_STATUSES.has(gamePlayerData.turn_status);
}

// Mid-action Skip gate. Once the player has dropped into
// LODGING/GHOSTING/etc with a juice/target/add/remove action_status,
// the in-picker affordance disappears (status !== TAKING_TURN_ID) and
// the player is mid-action. The window-level control here is a SKIP
// (api.clickNext → skip_button → finish_action), the only thing the
// engine actually does on this path. active_player gate is
// non-negotiable, same as the End Turn branch.
function shouldShowMidActionSkip(gamePlayerData, gameData) {
    if (!gamePlayerData || !gameData) return false;
    if (gameData.active_player !== gamePlayerData.id) return false;
    if (!JUICED_SUB_STATUSES.has(gamePlayerData.status)) return false;
    return JUICED_SUB_ACTION_STATUSES.has(gamePlayerData.action_status);
}

// ActionWindow — Action HUD for the Desktop preset. Owns the
// turn-driving picker (ActionHex + ActionPicker) and lifts the End
// Turn affordance up to the window level so it stays visible across
// mode transitions (the source's in-picker End Turn vanished
// whenever the player dropped into a juiced sub-state like
// LODGING/WAIT_FOR_JUICE, because ActionSelector returned <></> for
// status !== TAKING_TURN_ID).
export function ActionWindow(props) {
    const { gamePlayerData, gameData, app } = props;
    const mode = computeMode(gamePlayerData);
    const showEndTurn = shouldShowEndTurn(gamePlayerData, gameData);
    const showMidActionSkip = shouldShowMidActionSkip(
        gamePlayerData, gameData);

    // ActionHex shows in chooser / waiting modes only. When the
    // hex is hidden (select mode, juiced sub-states), surface the
    // action the player has committed to so the window still
    // carries context — otherwise the player loses sight of what
    // they're doing the moment the rotation UI disappears.
    const showCurrentAction = (
        mode !== 'chooser' && mode !== 'waiting'
        && currentActionIds(gamePlayerData).length > 0);

    // Idle shell only when the window has nothing useful to show.
    // The earlier guard (`mode === 'idle' && !showEndTurn`) was too
    // aggressive: in a juiced sub-state with action_status outside
    // the End-Turn set (e.g. RENOVATING_ID + JUICE_ID) it
    // suppressed the Current Action banner too, blanking the window
    // mid-action — bug Michael flagged 2026-05-25. The mid-action
    // Skip control is also load-bearing here, so it joins the guard.
    if (mode === 'idle' && !showEndTurn && !showMidActionSkip
            && !showCurrentAction) {
        return (
            <div className={'action-window action-window--idle'}
                 data-testid={'action-window'}
                 data-mode={'idle'}/>
        );
    }

    const api = (app && app.game && app.game.seatedController
            && app.game.seatedController.gamePlayerApi)
        ? app.game.seatedController.gamePlayerApi : null;

    return (
        <div className={'action-window action-window--' + mode}
             data-testid={'action-window'}
             data-mode={mode}>
            <ActionWrapper>
                {(mode === 'chooser' || mode === 'waiting') && (
                    <ActionHex {...props}/>
                )}
                {showCurrentAction && (
                    <CurrentAction gamePlayerData={gamePlayerData}/>
                )}
                {mode !== 'idle' && (
                    <ActionPicker {...props}/>
                )}
                {(mode === 'chooser' || mode === 'waiting') && (
                    <ActionHexDial {...props}/>
                )}
                {showEndTurn && (
                    <EndTurnButton
                        gamePlayerData={gamePlayerData}
                        api={api}
                        extraClass={'action-done-container--below'}/>
                )}
                {showMidActionSkip && (
                    <EndTurnButton
                        gamePlayerData={gamePlayerData}
                        api={api}
                        label={MID_ACTION_SKIP_LABEL}
                        testIdBase={MID_ACTION_SKIP_TESTID_BASE}
                        onClick={api ? api.clickNext : undefined}
                        extraClass={'action-done-container--below'}/>
                )}
            </ActionWrapper>
        </div>
    );
}

export default ActionWindow;
